import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { MoreVertical, FileText, ArrowUpDown, ChevronUp, ChevronDown, Pencil, Trash2, MoveRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";

interface FormField {
  name: string;
  description: string;
  type: string;
  updateRules?: string;
  readScope: string;
  writeScope: string;
  readAgents?: number[];
  writeAgents?: number[];
}

interface SimpleAgentRef {
  id: number;
  agent_name: string;
}

interface AgentFormFieldsTableProps {
  formFields: FormField[];
  visibleFormFields: FormField[];
  formSearchQuery: string;
  onSearchChange: (query: string) => void;
  formSortBy: "name" | "type" | "none";
  formSortDir: "asc" | "desc";
  onToggleSort: (column: "name" | "type") => void;
  formFieldsPage: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onFieldClick: (index: number) => void;
  onFieldRemove: (index: number) => void;
  onFieldMove: (fieldIndex: number, targetAgentId: number) => void;
  onBulkMove: (fieldNames: string[], targetAgentId: number) => void;
  onBulkDelete: (fieldNames: string[]) => void;
  subagents?: SimpleAgentRef[];
  isVersionView?: boolean;
}

const AgentFormFieldsTable: React.FC<AgentFormFieldsTableProps> = ({
  formFields,
  visibleFormFields,
  formSearchQuery,
  onSearchChange,
  formSortBy,
  formSortDir,
  onToggleSort,
  formFieldsPage,
  onPageChange,
  itemsPerPage,
  onFieldClick,
  onFieldRemove,
  onFieldMove,
  onBulkMove,
  onBulkDelete,
  subagents,
  isVersionView = false,
}) => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const toggleFieldSelection = (fieldName: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((name) => name !== fieldName)
        : [...prev, fieldName]
    );
  };

  const toggleAllFields = () => {
    if (selectedFields.length === visibleFormFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(visibleFormFields.map((f) => f.name));
    }
  };

  const handleBulkMove = (targetAgentId: number) => {
    if (selectedFields.length === 0) return;
    onBulkMove(selectedFields, targetAgentId);
    setSelectedFields([]);
  };

  const handleBulkDelete = () => {
    if (selectedFields.length === 0) return;
    onBulkDelete(selectedFields);
    setSelectedFields([]);
  };

  if (formFields.length === 0) {
    return (
      <Card className="border-border">
        <CardContent className="p-12">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-muted-foreground" />
            </div>
            <h4 className="text-base font-medium text-foreground mb-2">No form fields configured</h4>
            <p className="text-sm text-muted-foreground">
              Add fields to collect information during agent conversations
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-3">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                {selectedFields.length > 0
                  ? `${selectedFields.length} selected`
                  : `${visibleFormFields.length} ${visibleFormFields.length === 1 ? "field" : "fields"}`}
              </div>
              {selectedFields.length > 0 && !isVersionView && (
                <>
                  {subagents && subagents.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <MoveRight className="w-4 h-4" />
                          Move To...
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {subagents.map((subagent) => (
                          <DropdownMenuItem
                            key={subagent.id}
                            onClick={() => handleBulkMove(subagent.id)}
                          >
                            {subagent.agent_name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-red-600 hover:text-red-600 hover:bg-red-50"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </>
              )}
            </div>
            <div className="flex-1" />
            <div className="w-64">
              <Input
                value={formSearchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search fields..."
              />
            </div>
          </div>

          {visibleFormFields.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              No matching fields
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedFields.length === visibleFormFields.length && visibleFormFields.length > 0}
                      onCheckedChange={toggleAllFields}
                      disabled={isVersionView}
                    />
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => onToggleSort("name")}
                    >
                      Field Name
                      {formSortBy !== "name" ? (
                        <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : formSortDir === "asc" ? (
                        <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => onToggleSort("type")}
                    >
                      Type
                      {formSortBy !== "type" ? (
                        <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : formSortDir === "asc" ? (
                        <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleFormFields
                  .slice((formFieldsPage - 1) * itemsPerPage, formFieldsPage * itemsPerPage)
                  .map((field, idx) => {
                    const originalIndex = formFields.indexOf(field);
                    return (
                      <TableRow
                        key={`${field.name}-${idx}`}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedFields.includes(field.name)}
                            onCheckedChange={() => toggleFieldSelection(field.name)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isVersionView}
                          />
                        </TableCell>
                        <TableCell 
                          className={isVersionView ? "font-mono" : "font-mono cursor-pointer"}
                          onClick={() => !isVersionView && onFieldClick(originalIndex)}
                        >
                          {field.name}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                            {field.type}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                              field.readScope === "public" 
                                ? "bg-maitai/10 text-maitai border border-maitai/20" 
                                : field.readScope === "custom"
                                ? "bg-blue-100 text-blue-700 border border-blue-200"
                                : "bg-gray-100 text-gray-700 border border-gray-200"
                            }`}>
                              Read
                            </span>
                            <span className="text-muted-foreground">/</span>
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                              field.writeScope === "public" 
                                ? "bg-maitai/10 text-maitai border border-maitai/20" 
                                : field.writeScope === "custom"
                                ? "bg-blue-100 text-blue-700 border border-blue-200"
                                : "bg-gray-100 text-gray-700 border border-gray-200"
                            }`}>
                              Write
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{field.description || "-"}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isVersionView) onFieldClick(originalIndex);
                                }}
                                disabled={isVersionView}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              {!isVersionView && subagents && subagents.length > 0 && (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <MoveRight className="mr-2 h-4 w-4" />
                                    Move to...
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent onClick={(e) => e.stopPropagation()}>
                                    {subagents.map((subagent) => (
                                      <DropdownMenuItem
                                        key={subagent.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onFieldMove(originalIndex, subagent.id);
                                        }}
                                      >
                                        {subagent.agent_name}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isVersionView) onFieldRemove(originalIndex);
                                }}
                                disabled={isVersionView}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {visibleFormFields.length > itemsPerPage && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="flex items-center gap-6">
            <div className="text-sm text-muted-foreground">
              {(() => {
                const total = visibleFormFields.length;
                const clampedPage = Math.min(formFieldsPage, Math.ceil(total / itemsPerPage) || 1);
                const startItem = (clampedPage - 1) * itemsPerPage + 1;
                const endItem = Math.min(clampedPage * itemsPerPage, total);
                return `Showing ${startItem} to ${endItem} of ${total} fields`;
              })()}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-maitai/10 text-maitai border border-maitai/20">
                  Public
                </span>
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                  Custom
                </span>
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                  Private
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, formFieldsPage - 1))}
              disabled={formFieldsPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {formFieldsPage} of {Math.ceil(visibleFormFields.length / itemsPerPage)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(Math.ceil(visibleFormFields.length / itemsPerPage), formFieldsPage + 1))}
              disabled={formFieldsPage === Math.ceil(visibleFormFields.length / itemsPerPage)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentFormFieldsTable;

